<script setup lang="ts">
import Pdf from "@/components/Pdf.vue"
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import VarsityInlineConfirmGroup from "@/components/VarsityInlineConfirmGroup.vue"
import { useVarsityAppConfig } from "@/composables"
import { createInlineConfirm } from "@/composables/createInlineConfirm"
import type { CompanyProfile } from "@/model/app/company-profile"
import type { ApproveLoginJobActivityTask } from "@/model/staff/approve-login-job-activity-task"
import { useApp, useHttpClient } from "@/vf"
import type { TasksProps } from "../tasks-props"

const props = defineProps<TasksProps<ApproveLoginJobActivityTask>>()
const emit = defineEmits<{
    (e: "updated"): void
}>()

const http = useHttpClient()

const { apiLink } = useApp()

const inlineConfirmAccept = createInlineConfirm({
    async action() {
        await http.post(`/staff-admin/login-activity/approve/${props.task.id}/accept`)
    },
    afterAction() {
        emit("updated")
    },
})

const inlineConfirmDecline = createInlineConfirm({
    async action() {
        await http.post(`/staff-admin/login-activity/approve/${props.task.id}/decline`, {})
    },
    afterAction() {
        emit("updated")
    },
})
const { appConfig } = useVarsityAppConfig()

function hasSignature(companyProfile: CompanyProfile | undefined): boolean {
    if (!companyProfile) {
        return false
    }
    return !!appConfig.value.staffAccount?.hasSignatureFor?.map(c => c.id).includes(companyProfile.id)
}
</script>

<template>
    <div class="p-4">
        <div class="row">
            <div v-if="props.task.attachments!.length > 0" class="col-md-6">
                <template v-for="attachment in props.task.attachments" :key="attachment.id">
                    <div class="mt-3" v-if="attachment.mimeType == 'application/pdf'">
                        <pdf :file="`/tasks/task/${props.task.id}/attachment/${attachment.id}/download`"></pdf>
                    </div>
                    <div class="img-fluid" v-if="attachment.mimeType?.startsWith('image/')">
                        <img
                            :src="apiLink(`/tasks/task/${props.task.id}/attachment/${attachment.id}/download`)"
                            :alt="attachment.originalFilename"
                            class="img-fluid"
                        />
                    </div>
                </template>
            </div>
            <div v-if="props.task.state === 'open'" class="col-md-6 mt-4">
                <div v-if="viewMode === 'customer'" class="text-center my-5">
                    {{ $t("@tasks:tasks.approve_login_job_activity.waiting_approval_staff_member") }}
                </div>
                <div v-else-if="viewMode === 'admin'">
                    <div class="alert alert-danger" v-if="!hasSignature(task.companyProfile)">
                        {{ $t("@tasks:tasks.approve_login_job_activity.no_signature_found") }}
                    </div>
                    <VarsityInlineConfirmGroup v-if="hasSignature(task.companyProfile)">
                        <VarsityInlineConfirmButton
                            :controller="inlineConfirmAccept"
                            class="btn btn-success btn-shape-skewed"
                        >
                            {{
                                $t("@tasks:tasks.approve_login_job_activity.accept_button", {
                                    name: props.task.loginJobActivity?.jobActivity?.name,
                                })
                            }}
                        </VarsityInlineConfirmButton>
                        <VarsityInlineConfirmButton
                            :controller="inlineConfirmDecline"
                            class="btn btn-danger btn-shape-skewed"
                        >
                            {{
                                $t("@tasks:tasks.approve_login_job_activity.decline_button", {
                                    name: props.task.loginJobActivity?.jobActivity?.name,
                                })
                            }}
                        </VarsityInlineConfirmButton>

                        <VarsityInlineConfirm :controller="inlineConfirmAccept" :spinner="true">
                            <template #confirmation>
                                {{ $t("@tasks:tasks.approve_login_job_activity.accept_confirm") }}
                            </template>
                            <template #success>
                                {{ $t("@tasks:tasks.approve_login_job_activity.accept_successfull") }}
                            </template>
                        </VarsityInlineConfirm>

                        <VarsityInlineConfirm :controller="inlineConfirmDecline">
                            <template #confirmation>
                                {{ $t("@tasks:tasks.approve_login_job_activity.decline_confirm") }}
                            </template>
                            <template #success>
                                {{ $t("@tasks:tasks.approve_login_job_activity.decline_successfull") }}
                            </template>
                        </VarsityInlineConfirm>
                    </VarsityInlineConfirmGroup>
                </div>
            </div>

            <div v-else-if="props.task.state === 'closed'" class="text-center my-4">
                {{ $t("@tasks:tasks.approve_login_job_activity.task_approved") }}
            </div>

            <div v-else-if="props.task.state === 'rejected'" class="text-center my-4">
                {{ $t("@tasks:tasks.approve_login_job_activity.task_rejected") }}
            </div>
        </div>
    </div>
</template>
